import { useCallback, useEffect, useState } from 'react'
import { useLogger } from '../../../components/Logger/hooks/useLogger'

export type mediaDevicesParams = {
  constraints?: MediaStreamConstraints
  onError?: (error: unknown) => void
}

const devicesAreEqual = (prev: MediaDeviceInfo[], next: MediaDeviceInfo[]): boolean => {
  if (prev.length !== next.length) return false

  const prevIds = prev
    .map((d) => d.deviceId)
    .sort()
    .join(',')
  const nextIds = next
    .map((d) => d.deviceId)
    .sort()
    .join(',')

  return prevIds === nextIds
}

export const useMediaDevices = ({ onError, constraints }: mediaDevicesParams = {}) => {
  const [devices, setDevices] = useState<MediaDeviceInfo[] | null>(null)
  const [loading, setLoading] = useState(true)
  const logger = useLogger()
  const onChange = useCallback(async () => {
    navigator.mediaDevices
      .getUserMedia(constraints)
      .then(() => {
        navigator.mediaDevices.enumerateDevices().then((newDevices) => {
          setDevices((prevDevices) => {
            if (prevDevices && devicesAreEqual(prevDevices, newDevices)) {
              return prevDevices
            }
            return newDevices
          })
        })
      })
      .catch(onError)
      .finally(() => setLoading(false))
  }, [constraints, onError])

  const changedInBackground = useCallback(async () => {
    await logger.info('[useMediaDevice] Media devices change detected in background, updating device list.')
    await onChange()
  }, [onChange, logger])

  useEffect(() => {
    navigator.mediaDevices.addEventListener('devicechange', changedInBackground)
    onChange()
    return () => {
      setLoading(false)
      navigator.mediaDevices.removeEventListener('devicechange', changedInBackground)
    }
  }, [onChange, changedInBackground])

  return { devices, loading }
}
